import { useState } from 'react'
import { useNotify } from 'ra-core'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@internal/ui-kit/components/ui/drawer'
import { Input } from '@internal/ui-kit/components/ui/input'
import { Label } from '@internal/ui-kit/components/ui/label'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'

/**
 * `createPointsTransaction` (`retool-exports/user-detail/queries.json`,
 * `POST v2/admin/users/{user_id}/points/transactions`) — ticket feedback:
 * deferred in the original PR (not in this ticket's own acceptance
 * criteria), now requested. `amount` is signed (negative subtracts points,
 * per the reviewer's own wording); `reference_type` is always `"manual"` and
 * `reference_id` is the acting admin's own id, matching the source export's
 * `adminUser.data.user_id` — same fixed-value treatment `CreateEventDrawer`
 * gives `form_slug`'s own non-user-facing defaults.
 */
export function AddPointsTransactionDrawer({
  userId,
  accountId,
  onCreated,
}: {
  userId: string
  accountId: string | null | undefined
  onCreated: () => void
}) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()

  const canSubmit = Boolean(accountId && amount && title)

  const reset = () => {
    setAmount('')
    setTitle('')
    setSubtitle('')
  }

  const submit = async () => {
    if (!accountId) return
    setIsSubmitting(true)
    try {
      await httpClient(`/v2/admin/users/${userId}/points/transactions`, {
        method: 'POST',
        body: JSON.stringify({
          account_id: accountId,
          amount: Number(amount),
          title,
          subtitle: subtitle || undefined,
          reference_id: userId,
          reference_type: 'manual',
        }),
      })
      notify('Points transaction added', { type: 'success' })
      reset()
      setOpen(false)
      onCreated()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not add this points transaction.'), { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen} direction="right">
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        Add transaction
      </Button>
      <DrawerContent className="w-full sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>Add points transaction</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="points-transaction-amount">Amount</Label>
            <Input
              id="points-transaction-amount"
              type="number"
              placeholder="Use a negative number to subtract points"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="points-transaction-title">Title</Label>
            <Input id="points-transaction-title" value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="points-transaction-subtitle">Subtitle</Label>
            <Input
              id="points-transaction-subtitle"
              value={subtitle}
              onChange={(event) => setSubtitle(event.target.value)}
            />
          </div>
        </div>
        <DrawerFooter>
          <Button type="button" disabled={!canSubmit || isSubmitting} onClick={submit}>
            Add transaction
          </Button>
          <DrawerClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
